/** Credentials and preferences persistence. Corrupt storage never breaks boot. */

import { type Credentials, DEFAULT_PREFS, DEFAULTS, PREF_KEYS, type Prefs } from './defaults.ts';

const CREDS_KEY = 'turntable.creds.v1';
const PREFS_KEY = 'turntable.prefs.v1';

function readJSON(key: string): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode, quota, or no storage at all — the app still runs */
  }
}

const str = (v: unknown, fallback: string): string => (typeof v === 'string' ? v.trim() : fallback);

export function loadCreds(): Credentials {
  const saved = readJSON(CREDS_KEY);
  return {
    GOOGLE_CLIENT_ID: str(saved.GOOGLE_CLIENT_ID, '') || DEFAULTS.GOOGLE_CLIENT_ID.trim(),
    YOUTUBE_API_KEY: str(saved.YOUTUBE_API_KEY, '') || DEFAULTS.YOUTUBE_API_KEY.trim(),
  };
}

export function saveCreds(creds: Credentials): void {
  writeJSON(CREDS_KEY, creds);
}

export function clearCreds(): void {
  try {
    localStorage.removeItem(CREDS_KEY);
  } catch {
    /* ignore */
  }
}

export function loadPrefs(): Prefs {
  return { ...DEFAULT_PREFS, ...readJSON(PREFS_KEY) } as Prefs;
}

/** Only the user-facing keys are written back. */
export function savePrefs(prefs: Prefs): void {
  const out: Partial<Prefs> = {};
  for (const k of PREF_KEYS) out[k] = prefs[k] as never;
  writeJSON(PREFS_KEY, out);
}
